import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Driver } from '../domain/driver/Driver';
import { GeoFence } from '../domain/geoFence/GeoFence';
import { Plan } from '../domain/plan/Plan';
import { PlanFlat } from '../domain/plan/PlanFlat';
import { PlanStatus } from '../domain/plan/PlanStatus';
import { PlansCalendar } from '../domain/plan/PlansCalendar';
import { Project } from '../domain/project/Project';
import { Stop } from '../domain/stop/Stop';
import { planRepository } from '../repositories/plan';
import { PlanOptimizationData, PlanRepository } from '../repositories/plan/PlanRepository';
import eventsPanel from '../services/events';
import { INTERCOM_EVENTS, intercom } from '../services/intercom';

export class PlanService {
  constructor(private planRepository: PlanRepository) {}

  async fetch({ planId, project }: { planId: string; project: Project }): Promise<PlanFlat> {
    const plan = await this.planRepository.fetch({ planId, project });
    return plan;
  }

  async search({
    project,
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    project: Project;
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<PlanFlat>> {
    const plansPagination = await this.planRepository.search({
      projectId: project.id,
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });

    return plansPagination;
  }

  async fetchFlatPlans({
    project,
    predicates,
  }: {
    project: Project;
    predicates?: Array<SearchPredicate>;
  }): Promise<Array<PlanFlat>> {
    const flatStops = await this.planRepository.fetchFlatPlans({
      projectId: project.id,
      predicates,
    });

    return flatStops;
  }

  async create({ plan, project }: { plan: Plan; project: Project }): Promise<Plan> {
    const newPlan = await this.planRepository.create({ plan, project });
    intercom.track(INTERCOM_EVENTS.plan.created, { id: newPlan.id });
    eventsPanel.planCreated();
    return newPlan;
  }

  async createWithAssets({ plan, project }: { plan: Plan; project: Project }): Promise<Plan> {
    const newPlan = await this.planRepository.createWithAssets({ plan, project });
    intercom.track(INTERCOM_EVENTS.plan.created, { id: newPlan.id });
    eventsPanel.planCreated();
    return newPlan;
  }

  async save({ plan, hasTraffic }: { plan: PlanFlat; hasTraffic: boolean }): Promise<PlanFlat> {
    const updatedPlan = await this.planRepository.save({ plan, hasTraffic });
    return updatedPlan;
  }

  async updateGeoFences({ plan, geo_fences }: { plan: PlanFlat; geo_fences: GeoFence[] }): Promise<PlanFlat> {
    const updatedPlan = await this.planRepository.updateGeoFences({ plan, geo_fences });
    return updatedPlan;
  }

  async updateFlatPlansStatus({
    flatPlans,
    status,
  }: {
    flatPlans: Array<PlanFlat>;
    status: PlanStatus;
  }): Promise<Array<PlanFlat>> {
    const updatedFlatPlans = await this.planRepository.updateFlatPlansStatus({ flatPlans, status });

    eventsPanel.planUpdated(status);

    return updatedFlatPlans;
  }

  async deleteFlatPlans(flatPlans: Array<PlanFlat>): Promise<void> {
    return this.planRepository.deleteFlatPlans(flatPlans);
  }

  async duplicatePlan({
    sourcePlan,
    duplicates,
  }: {
    sourcePlan: PlanFlat;
    duplicates: { label: string; status: PlanStatus; execution_date: Date }[];
  }): Promise<Array<PlanFlat | null>> {
    return this.planRepository.duplicatePlan({ sourcePlan, duplicates });
  }

  async getPlanDrivers({ planId, project }: { planId: string; project: Project }): Promise<Array<Driver>> {
    return this.planRepository.getPlanDrivers({ planId, project });
  }

  async getPlanStops({ planId, project }: { planId: string; project: Project }): Promise<Array<Stop>> {
    return this.planRepository.getPlanStops({ planId, project });
  }

  async getPlanOptimization({ planId }: { planId: string }): Promise<PlanOptimizationData> {
    return this.planRepository.getPlanOptimization({ planId });
  }

  async optimize({
    plan,
    project,
    keepCurrentAssignment = false,
    isOwner = false,
  }: {
    plan: PlanFlat;
    project: Project;
    keepCurrentAssignment?: boolean;
    isOwner?: boolean;
  }): Promise<Plan> {
    const updatedPlan = await this.planRepository.optimize({ plan, project, keepCurrentAssignment });

    if (isOwner) {
      eventsPanel.planOptimized({});
    }

    return updatedPlan;
  }

  async getPlanGeoFences({ planId }: { planId: string }): Promise<Array<GeoFence>> {
    return this.planRepository.getPlanGeoFences({ planId });
  }

  async getPlansCalendar({
    startDate,
    endDate,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    projectId: string;
  }): Promise<PlansCalendar[]> {
    const flatStops = await this.planRepository.getPlansCalendar({
      startDate,
      endDate,
      projectId,
    });

    return flatStops;
  }
}

export const planService = new PlanService(planRepository);
